import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import './index.css';
import App from './App.tsx';
import { registerServiceWorker } from './lib/serviceWorker';
import { measureWebVitals, measureResourceTiming } from './lib/performance';
import { runAccessibilityAudit } from './lib/accessibility-test';

// Smooth scroll polyfill for Safari < 15.4 and older browsers
if (!('scrollBehavior' in document.documentElement.style)) {
  const originalScrollTo = window.scrollTo;
  window.scrollTo = function(options: ScrollToOptions | number, y?: number) {
    if (typeof options === 'object' && options.behavior === 'smooth') {
      const startY = window.scrollY;
      const targetY = options.top ?? 0;
      const distance = targetY - startY;
      const duration = 500;
      let startTime: number | null = null;

      function step(currentTime: number) {
        if (!startTime) startTime = currentTime;
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const easeInOutCubic = progress < 0.5
          ? 4 * progress * progress * progress
          : 1 - Math.pow(-2 * progress + 2, 3) / 2;
        
        originalScrollTo.call(window, 0, startY + distance * easeInOutCubic);
        if (elapsed < duration) requestAnimationFrame(step);
      }
      requestAnimationFrame(step);
    } else {
      originalScrollTo.call(window, options as number, y as number);
    }
  };
}
createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <BrowserRouter>
      <App />
    </BrowserRouter>
  </StrictMode>
);

// Register service worker for caching
registerServiceWorker();

// Development-only features
if (import.meta.env.DEV) {
  // Measure performance
  measureWebVitals();
  measureResourceTiming();
  
  // Run accessibility audit after page load
  window.addEventListener('load', () => {
    setTimeout(() => {
      runAccessibilityAudit();
    }, 1000);
  });
}